// Model Route UI types — all shapes are fed by the Canvas API (src/api/canvas)
// through ./api.ts mapping; components only consume these UI shapes.

export type RouteStatus = 'activated' | 'suspended'

export const routeStatusMap: Record<
  RouteStatus,
  { labelKey: string; label: string; style: string }
> = {
  activated: { labelKey: 'status.activated', label: 'Activated', style: 'text-success' },
  suspended: { labelKey: 'status.suspended', label: 'Suspended', style: 'text-primary' },
}

export interface ModelRoute {
  resID: string
  name: string
  model: string
  status: RouteStatus
  /** Accumulated settled Credits — decimal string, display-ready. */
  cost: string
  createdAt: string
  lastModified: string
  description: string
}

export interface ModelRouteKpi {
  totalRoutes: number
  activatedRoutes: number
  todayRequests: number
  avgResponseSec: number
  todayTokens: number
  /** Settled Credits (UTC day) — decimal string, display-ready. */
  todayCost: string
}

export type RouteModelType = 'MaaS' | 'Instance' | 'Auto Deployment'

export interface RouteModel {
  model: string
  /** Vendor display name resolved from GET /models/vendors; '-' when unknown (vendor 0). */
  provider: string
  capabilities: string[]
  /** Per-1M-token prices — decimal strings, display-ready. */
  inputPrice: string
  outputPrice: string
}

export interface RouteApiAccess {
  authMode: string
  accessModes: string[]
  accessUrl: string
  /** Data for the runnable curl example block (rendered per-shell by ApiAccessExample). */
  example: {
    url: string
    model: string
    protocol: 'openai' | 'claude'
  }
  /**
   * Credential status for display (Active/Revoked). The key itself is never retrievable
   * (one-time on create/rotate only — backend security policy).
   */
  credentialEnabled: boolean
}

export interface RouteBillingSummary {
  totalRequests: number
  totalTokens: number
  /** Settled Credits — decimal string, display-ready. */
  totalCost: string
}

export interface RouteBillingRecord {
  /** Route display name; '-' when the route has been deleted (historical records). */
  route: string
  routeId: string
  model: string
  /** Protocol label of the settled window: 'OpenAI' | 'Claude' | '-' (legacy unknown). */
  routeType: string
  endpoint: string
  tokenIn: number
  tokenOut: number
  latencySec: number
  firstTokenSec: number
  /** Settled Credits — decimal string, display-ready. */
  cost: string
  time: string
}

export interface RouteMonitorTopModel {
  model: string
  inputTokens: number
  outputTokens: number
  cachedTokens: number
}

export interface RouteMonitorSummary {
  totalInputTokens: number
  totalOutputTokens: number
  totalCachedTokens: number
  topModels: RouteMonitorTopModel[]
}

export interface Paginated<T> {
  items: T[]
  total: number
}

// --- Create / edit flow types ---

export const ROUTE_TYPES: RouteModelType[] = ['MaaS', 'Instance', 'Auto Deployment']

// Display-only i18n keys (modelRoute ns). RouteModelType values are logic/API
// identifiers — translate at render time, never change the values themselves.
export const ROUTE_TYPE_LABEL_KEYS: Record<RouteModelType, string> = {
  MaaS: 'routeTypes.maas',
  Instance: 'routeTypes.instance',
  'Auto Deployment': 'routeTypes.autoDeployment',
}

export interface CatalogModel {
  /** Public model ID (model-*). */
  id: string
  model: string
  /** Vendor display name resolved from GET /models/vendors; '-' when unknown. */
  provider: string
  capabilities: string[]
  /** Per-1M-token prices — decimal strings, display-ready. */
  inputPrice: string
  outputPrice: string
  /** API modes the model supports — passed through to the route binding on submit. */
  modes: ('openai' | 'claude')[]
}

export type AccessMode = 'auto' | 'manual'
